import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import CreateInvitation from './CreateInvitation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function AdminDashboard() {
  const [responses, setResponses] = useState(null);
  const [activeTab, setActiveTab] = useState('responded'); // 'responded' or 'pending'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'invited', 'not-invited'
  const navigate = useNavigate();

  useEffect(() => {
    fetchResponses();
  }, []);

  const fetchResponses = async () => {
    try {
      const result = await api.get('/responses');
      console.log('API Response:', result.data); // Debug log
      if (result.data && typeof result.data === 'string') {
        setResponses(JSON.parse(result.data));
      } else {
        setResponses(result.data);
      }
    } catch (error) {
      console.error('Error fetching responses:', error); // Debug log
      if (error.response?.status === 401) {
        // Unauthorized - redirect to login
        navigate('/login');
      } else {
        alert('Error fetching responses');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAdminAuthenticated');
    localStorage.removeItem('adminToken');
    navigate('/login');
  };

  const handleDelete = async (secretCode) => {
    if (window.confirm('Are you sure you want to delete this invitation?')) {
      try {
        await api.post('/invitation/delete', { secretCode });
        fetchResponses();
      } catch (error) {
        if (error.response?.status === 401) {
          navigate('/login');
        } else {
          alert('Error deleting invitation');
        }
      }
    }
  };

  const handleCopyLink = async (secretCode) => {
    const invitationUrl = `${window.location.origin}/invite/${secretCode}`;
    try {
      await navigator.clipboard.writeText(invitationUrl);
      alert('Invitation link copied to clipboard!');
    } catch (error) {
      alert('Failed to copy link');
    }
  };

  const handleToggleInvited = async (secretCode, currentStatus) => {
    try {
      await api.post('/invitation/status', {
        secretCode,
        isInvited: !currentStatus
      });
      fetchResponses(); // Refresh the list
    } catch (error) {
      alert('Error updating invitation status');
    }
  };

  // Update filter function to handle both name and status
  const filterGuests = (guests) => {
    if (!guests) return [];
    console.log('Filtering guests:', guests); // Debug log
    return guests.filter(guest => {
      const matchesName = !searchTerm || 
        guest.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'invited' && guest.isInvited) ||
        (statusFilter === 'not-invited' && !guest.isInvited);

      return matchesName && matchesStatus;
    });
  };

  // Sort responded guests by response time (most recent first)
  const sortByResponseTime = (guests) => {
    if (!guests) return [];
    // Create a sorted copy of the guests array
    return [...guests].sort((a, b) => {
      // Parse dates and compare them (newer dates first)
      const dateA = new Date(a.respondedAt);
      const dateB = new Date(b.respondedAt);
      return dateB - dateA;
    });
  };

  if (!responses || !responses.guests || !responses.pendingGuests) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-dancing text-primary-600">Wedding Dashboard</h2>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Logout
          </button>
        </div>

        <div className="flex gap-6 mb-8">
          <div className="w-1/2">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-gray-500 text-xs font-semibold mb-1">Total Invitations</h3>
                <p className="text-2xl font-bold text-gray-900">{responses.total}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-gray-500 text-xs font-semibold mb-1">Total Responses</h3>
                <p className="text-2xl font-bold text-blue-600">{responses.totalResponses || 0}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-gray-500 text-xs font-semibold mb-1">Attending</h3>
                <p className="text-2xl font-bold text-green-600">{responses.attending}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-gray-500 text-xs font-semibold mb-1">Not Attending</h3>
                <p className="text-2xl font-bold text-red-600">{responses.notAttending}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-gray-500 text-xs font-semibold mb-1">Pending</h3>
                <p className="text-2xl font-bold text-yellow-600">{responses.pending}</p>
              </div>
            </div>
          </div>

          <div className="w-1/2">
            <CreateInvitation onInvitationCreated={fetchResponses} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-800">Guest List</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('responded')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  activeTab === 'responded'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Responded
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  activeTab === 'pending'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Pending
              </button>
            </div>
          </div>

          {/* Add search input */}
          <div className="px-6 py-3 border-b border-gray-200">
            <div className="flex gap-4 items-center">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {activeTab === 'pending' && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500">Status:</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none"
                  >
                    <option value="all">All</option>
                    <option value="invited">Invited</option>
                    <option value="not-invited">Not Invited</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            {activeTab === 'responded' ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] w-1/4">
                      Name
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">
                      Response
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Response Time
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Secret Code
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortByResponseTime(filterGuests(responses.guests)).map((guest) => (
                    <tr key={guest.secretCode} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 text-left">
                        {guest.name}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                          guest.isAttending 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {guest.isAttending ? 'Attending' : 'Not Attending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 text-center">
                        <div>{new Date(guest.respondedAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}</div>
                        <div className="text-xs text-gray-400">{new Date(guest.respondedAt).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 font-mono text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span>{guest.secretCode}</span>
                          <button
                            onClick={() => handleCopyLink(guest.secretCode)}
                            className="inline-flex items-center p-1 text-gray-500 hover:text-primary-600 transition-colors"
                            title="Copy invitation link"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[35%]">
                      Name
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[25%]">
                      Secret Code
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[20%]">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[20%]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filterGuests(responses.pendingGuests).map((guest) => (
                    <tr key={guest.secretCode} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 text-left">
                        {guest.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 font-mono text-center">
                        {guest.secretCode}
                      </td>
                      <td className="px-6 py-4 text-sm text-center">
                        <button
                          onClick={() => handleToggleInvited(guest.secretCode, guest.isInvited)}
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            guest.isInvited 
                              ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                              : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          }`}
                        >
                          {guest.isInvited ? 'Invited' : 'Not Invited'}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleCopyLink(guest.secretCode)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            Copy Link
                          </button>
                          <button
                            onClick={() => handleDelete(guest.secretCode)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Show message when no results found */}
          {((activeTab === 'responded' && filterGuests(responses.guests).length === 0) ||
            (activeTab === 'pending' && filterGuests(responses.pendingGuests).length === 0)) && (
            <div className="px-6 py-4 text-center text-gray-500">
              No guests found matching the current filters
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard; 